import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { Task } from '../../models/task';
import { duplicateTask } from '../../services/api/taskServices';
import { AppButton } from '../app/button/appButton';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { AutocompleteOption } from '../../models/autocompleteOption';
import { useGetProjects } from '../../hooks/useGetProjects';
import { generateProjectLabel } from '../../services/projectService';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import { NO_PROJECTS, PLEASE_TYPE } from '../../constants';
import { AppRow } from '../../layout/appRow';
import useAppSnackbar from '../../hooks/useAppSnackbar';

const styles = {
  dialogActions: {
    p: 0,
    mt: 2,
  },
  taskName: {
    marginBottom: 1,
    fontWeight: 600,
    display: '-webkit-box',
    ' -webkit-box-orient': 'vertical',
    ' -webkit-line-clamp': 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dialogContent: {
    pb: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  optionBtn: {
    fontSize: '14px',
    mr: 0.5,
  },
  optionsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: 2,
  },
};

interface DuplicateFormFields {
  duplicateOnSameProject: true;
  project: AutocompleteOption | null;
}

interface DuplicateProp {
  isOpen: boolean;
  onClose(): void;
  task: Task | null;
  onRequestSuccessfully(): void;
}

export default function TaskDialogDuplicate({
  isOpen,
  onClose,
  task,
  onRequestSuccessfully,
}: DuplicateProp): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [projectFilter, setProjectFilter] = useState('');

  const methods = useForm<DuplicateFormFields>({
    defaultValues: {
      duplicateOnSameProject: true,
      project: null,
    },
  });

  const {
    control,
    watch,
    formState: { touchedFields, errors },
    setValue,
    reset,
    handleSubmit,
  } = methods;

  const { projects, isLoading: isLoadingProjects } = useGetProjects(
    projectFilter,
    !!touchedFields.project
  );
  const { onErrorSnackbar } = useAppSnackbar();

  const projectOptions = useMemo<AutocompleteOption[]>(() => {
    return projects.map((projectItem) => ({
      id: projectItem.id,
      label: generateProjectLabel(projectItem.name, projectItem.code),
    }));
  }, [projects]);

  const duplicateOnSameProject = watch('duplicateOnSameProject');
  const watchProject = watch('project');

  const isSaveButtonDisabled = useMemo(
    () => isLoading || (!duplicateOnSameProject && !watchProject),
    [duplicateOnSameProject, isLoading, watchProject]
  );

  const handleCloseDialog = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const duplicateSelectedTask = useCallback(
    (newTask) => {
      if (!task) return;

      const { project } = newTask;
      const { projectId } = task;

      setIsLoading(true);
      duplicateTask({
        taskId: task.id,
        projectId: project ? project.id : projectId,
      })
        .then(() => {
          onRequestSuccessfully();
          handleCloseDialog();
        })
        .catch((error) => {
          onErrorSnackbar(error as Error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [task, onRequestSuccessfully, handleCloseDialog, onErrorSnackbar]
  );

  return (
    <Dialog open={isOpen} sx={{ borderRadius: '5px' }}>
      <AppDialogTitle title="Duplicate Task" handleClose={handleCloseDialog} />
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(duplicateSelectedTask)}
          noValidate
          autoComplete="off"
        >
          <DialogContent sx={styles.dialogContent}>
            <Typography sx={styles.taskName}>Task: {task?.name}</Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <Typography variant="body2" sx={{ marginRight: 1 }}>
                Duplicate task on:
              </Typography>

              <Controller
                name="duplicateOnSameProject"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <AppRow sx={styles.optionsRow}>
                    <Button
                      variant="contained"
                      disabled={value}
                      sx={styles.optionBtn}
                      onClick={() => {
                        onChange(true);
                        setValue('project', null);
                      }}
                    >
                      Current project
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!value}
                      sx={styles.optionBtn}
                      onClick={() => onChange(false)}
                    >
                      Different project
                    </Button>
                  </AppRow>
                )}
              />
            </Box>

            {!duplicateOnSameProject && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ mb: 1 }}>Select a project:</Typography>
                <AppControlledAutocomplete
                  label="Project"
                  name="project"
                  options={projectOptions || []}
                  isLoading={isLoadingProjects}
                  searchOptions={setProjectFilter}
                  noOptionsText={NO_PROJECTS}
                  placeholder={PLEASE_TYPE}
                  isError={!!errors.project}
                  isRequired
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={styles.dialogActions}>
            <AppButton label="Cancel" onClick={handleCloseDialog} />
            <AppButton
              label="Save"
              isPrimary
              buttonType="submit"
              isLoading={isLoading}
              disabled={isSaveButtonDisabled}
            />
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
